import { getSiteOrigin } from "@/lib/siteUrl";
import { VOLUME_BOT_DEMO_VIDEO } from "@/config/volumeBotMedia";

const siteOrigin = getSiteOrigin();

/** Single-pool hero chips for DEX-specific volume bot landings. */
export const POOL_ONLY_CHIPS = {
  pumpfun: { name: "Pump.fun", icon: "/pools/pumpfun.png" },
  raydium: { name: "Raydium", icon: "/pools/raydium.png" },
  orca: { name: "Orca", icon: "/pools/orca.png" },
  meteora: { name: "Meteora", icon: "/pools/meteora.png" },
  idx: { name: "IDX Launchlab", icon: "/pools/idx.png" },
};

/** @deprecated Use POOL_ONLY_CHIPS.pumpfun */
export const PUMPFUN_ONLY_POOL_CHIP = POOL_ONLY_CHIPS.pumpfun;

export const POOL_LANDING_LINKS = Object.entries(POOL_ONLY_CHIPS).map(
  ([key, chip]) => ({
    key,
    name: chip.name,
    href: `/${key === "pumpfun" ? "pumpfun" : key}-volume-bot`,
  }),
);

export const VOLUME_BOT_POOLS = [
  {
    name: "PumpFun",
    icon: "/pools/pumpfun.png",
  },
  {
    name: "IDX Launchlab",
    icon: "/pools/idx.png",
  },
  {
    name: "Orca",
    icon: "/pools/orca.png",
  },
  {
    name: "LaunchLab",
    icon: "/pools/launchlab.png",
  },
  {
    name: "Meteora",
    icon: "/pools/meteora.png",
  },
  {
    name: "Moonit",
    icon: "/pools/moonit.png",
  },
  {
    name: "Bags",
    icon: "/pools/bags.png",
  },
];

/** Step 03 copy — keep in sync with public engine lineup (no V12 Turbo). */
const ENGINE_PICK_STEP_DETAIL =
  "Select up to three engines — Engine V12, V10, V4–V8, Mini engines, and more — for your launch stage and budget.";

const defaultHowItWorksSteps = [
  {
    step: "01",
    title: "Connect Wallet",
    detail:
      "Connect your Solana wallet and verify your active account before configuration.",
  },
  {
    step: "02",
    title: "Mint Address",
    detail:
      "Just add the mint address of your token , verify the name of your token",
  },
  {
    step: "03",
    title: "Configure Bot",
    detail: ENGINE_PICK_STEP_DETAIL,
  },
  {
    step: "04",
    title: "Launch & Track",
    detail:
      "Start the bot and monitor activity as it executes market-making actions.",
  },
];

function poolHowItWorksSteps(mintStepTitle, mintStepDetail) {
  return [
    {
      step: "01",
      title: "Connect Wallet",
      detail:
        "Connect your Solana wallet and verify your active account before configuration.",
    },
    {
      step: "02",
      title: mintStepTitle,
      detail: mintStepDetail,
    },
    {
      step: "03",
      title: "Pick your bot",
      detail: ENGINE_PICK_STEP_DETAIL,
    },
    {
      step: "04",
      title: "Fund & launch",
      detail:
        "Recharge in SOL, start the bot, and track activity from your dashboard.",
    },
  ];
}

const POOL_VOLUME_BOT_CONTENT = {
  pumpfun: {
    slug: "pumpfun",
    displayName: "Pump.fun",
    seoTitle: "Pump.fun Volume Bot — Boost Your Pump Coin",
    seoDescription:
      "On-chain volume for Pump.fun tokens. Paste your mint, pick an engine, fund your bot, and lift DexScreener visibility through graduation.",
    ogAlt: "Pump.fun Volume Bot — boost your pump coin on Solana",
    schemaAppName: "Pump.fun Volume Bot",
    eyebrow: "Pump.fun & PumpSwap automation",
    h1Primary: "Pump.fun Volume Bot",
    h1Secondary: "Creator fee back to you",
    heroParagraph:
      "Run an on-chain volume bot built for Pump.fun launches. Paste your token mint, choose an engine, and fund your bot in SOL or your own token. Increase trading activity and chart visibility on DexScreener while your coin is on the curve or after it migrates to PumpSwap and Raydium.",
    howToSubtitle: "From wallet connect to on-chain execution—built for pump coin launches.",
    mintStepTitle: "Paste pump coin mint",
    mintStepDetail: "Add your Pump.fun token mint address and confirm the symbol.",
    schemaMintStep: "Add your Pump.fun token mint address and confirm the symbol.",
    faq: [
      {
        question: "Does this work on Pump.fun before graduation?",
        answer:
          "Yes, for supported pools and routes. Paste the mint from your pump.fun coin page, pick an engine, and fund the bot—the same flow as our main volume bot.",
      },
      {
        question: "What happens after my coin graduates to PumpSwap or Raydium?",
        answer:
          "Keep the same bot and mint. Activity continues on the migrated pool so you do not need to rebuild your setup from scratch.",
      },
      {
        question: "Will this guarantee a bonding curve fill or trending?",
        answer:
          "No tool can guarantee fills or rankings. A volume bot increases on-chain trading activity that teams use alongside marketing for DexScreener and discovery visibility.",
      },
      {
        question: "Which engine is best for a new pump coin?",
        answer:
          "V4 or V6 for early curve and tighter budgets; V12, V10, V8, or Mini Buy / Mini Buy-Sell for aggressive launch windows and busier charts.",
      },
      {
        question: "How much SOL do I need?",
        answer:
          "Minimums depend on the engine and whether it is your first charge. The recharge modal shows the required amount before you send funds.",
      },
      {
        question: "Is it safe to stop the bot?",
        answer:
          "Yes. Stop from your dashboard and refund unused gas according to the product rules—same controls as the main Solana volume bot.",
      },
    ],
  },
  raydium: {
    slug: "raydium",
    displayName: "Raydium",
    seoTitle: "Raydium Volume Bot — Boost SPL Token Volume",
    seoDescription:
      "On-chain volume for Raydium AMM and CLMM tokens. Paste your mint, pick an engine, fund your bot, and lift DexScreener visibility.",
    ogAlt: "Raydium Volume Bot — boost SPL token volume on Raydium",
    schemaAppName: "Raydium Volume Bot",
    eyebrow: "Raydium AMM & CLMM automation",
    h1Primary: "Raydium Volume Bot",
    h1Secondary: "Market maker for your token on Raydium pools",
    heroParagraph:
      "Run an on-chain volume bot for tokens trading on Raydium. Paste your SPL mint, choose an engine, and fund your bot in SOL or your own token. Increase trading activity and chart visibility on DexScreener for Raydium AMM and concentrated liquidity pairs.",
    howToSubtitle:
      "From wallet connect to on-chain execution—built for Raydium-listed tokens.",
    mintStepTitle: "Paste token mint",
    mintStepDetail:
      "Add your token mint address for the Raydium pair you want to boost and confirm the symbol.",
    schemaMintStep:
      "Add your token mint address for your Raydium pool and confirm the symbol.",
    faq: [
      {
        question: "Does this work with Raydium AMM and CLMM pools?",
        answer:
          "Yes, for supported routes on Raydium. Paste your token mint, pick an engine, and fund the bot—the same engines and dashboard as our main Solana volume bot.",
      },
      {
        question: "Do I need an existing Raydium pool?",
        answer:
          "Your token should already trade on Raydium (or a supported route). If you are launching fresh, create liquidity first, then configure the bot for that mint.",
      },
      {
        question: "Will this guarantee trending on DexScreener?",
        answer:
          "No tool can guarantee rankings. A volume bot increases on-chain trading activity teams use alongside marketing for DexScreener and discovery visibility.",
      },
      {
        question: "Which engine is best for a Raydium launch?",
        answer:
          "V4 or V6 for steady, budget-friendly activity; V12, V10, V8, or Mini Buy / Mini Buy-Sell for aggressive launch windows and busier charts.",
      },
      {
        question: "How much SOL do I need?",
        answer:
          "Minimums depend on the engine and whether it is your first charge. The recharge modal shows the required amount before you send funds.",
      },
      {
        question: "Is it safe to stop the bot?",
        answer:
          "Yes. Stop from your dashboard and refund unused gas according to the product rules—same controls as the main Solana volume bot.",
      },
    ],
  },
  orca: {
    slug: "orca",
    displayName: "Orca",
    seoTitle: "Orca Volume Bot — Boost Whirlpool Token Volume",
    seoDescription:
      "Automated on-chain volume for tokens on Orca Whirlpools. Paste your mint, pick an engine, fund your bot, and improve DexScreener visibility on Orca pairs.",
    ogAlt: "Orca Volume Bot — boost token volume on Orca Whirlpools",
    schemaAppName: "Orca Volume Bot",
    eyebrow: "Orca Whirlpool automation",
    h1Primary: "Orca Volume Bot",
    h1Secondary: "Market maker for your token on Orca Whirlpools",
    heroParagraph:
      "Run an on-chain volume bot for tokens trading on Orca. Paste your SPL mint, choose an engine, and fund your bot in SOL or your own token. Increase trading activity and chart visibility on DexScreener for Orca Whirlpool and supported pairs.",
    howToSubtitle:
      "From wallet connect to on-chain execution—built for Orca-listed tokens.",
    mintStepTitle: "Paste token mint",
    mintStepDetail:
      "Add your token mint address for the Orca pool you want to boost and confirm the symbol.",
    schemaMintStep:
      "Add your token mint address for your Orca Whirlpool and confirm the symbol.",
    faq: [
      {
        question: "Does this work with Orca Whirlpools?",
        answer:
          "Yes, for supported Orca routes. Paste your token mint, pick an engine, and fund the bot—the same engines and dashboard as our main Solana volume bot.",
      },
      {
        question: "Do I need liquidity on Orca first?",
        answer:
          "Your token should already trade on Orca (or a supported route). Create a Whirlpool or add liquidity, then configure the bot for that mint.",
      },
      {
        question: "Will this guarantee trending on DexScreener?",
        answer:
          "No tool can guarantee rankings. A volume bot increases on-chain trading activity teams use alongside marketing for DexScreener and discovery visibility.",
      },
      {
        question: "Which engine is best for an Orca launch?",
        answer:
          "V4 or V6 for steady, budget-friendly activity; V12, V10, V8, or Mini Buy / Mini Buy-Sell for aggressive launch windows and busier charts.",
      },
      {
        question: "How much SOL do I need?",
        answer:
          "Minimums depend on the engine and whether it is your first charge. The recharge modal shows the required amount before you send funds.",
      },
      {
        question: "Is it safe to stop the bot?",
        answer:
          "Yes. Stop from your dashboard and refund unused gas according to the product rules—same controls as the main Solana volume bot.",
      },
    ],
  },
  meteora: {
    slug: "meteora",
    displayName: "Meteora",
    seoTitle: "Meteora Volume Bot — Boost DLMM Token Volume",
    seoDescription:
      "On-chain volume for Meteora DLMM and dynamic pools. Paste your mint, pick an engine, fund your bot, and lift DexScreener visibility.",
    ogAlt: "Meteora Volume Bot — boost token volume on Meteora pools",
    schemaAppName: "Meteora Volume Bot",
    eyebrow: "Meteora DLMM & dynamic pools",
    h1Primary: "Meteora Volume Bot",
    h1Secondary: "Market maker for your token on Meteora pools",
    heroParagraph:
      "Run an on-chain volume bot for tokens trading on Meteora. Paste your SPL mint, choose an engine, and fund your bot in SOL or your own token. Increase trading activity and chart visibility on DexScreener for Meteora DLMM and dynamic liquidity pools.",
    howToSubtitle:
      "From wallet connect to on-chain execution—built for Meteora-listed tokens.",
    mintStepTitle: "Paste token mint",
    mintStepDetail:
      "Add your token mint address for the Meteora pool you want to boost and confirm the symbol.",
    schemaMintStep:
      "Add your token mint address for your Meteora pool and confirm the symbol.",
    faq: [
      {
        question: "Does this work with Meteora DLMM pools?",
        answer:
          "Yes, for supported Meteora routes. Paste your token mint, pick an engine, and fund the bot—the same engines and dashboard as our main Solana volume bot.",
      },
      {
        question: "Do I need an existing Meteora pool?",
        answer:
          "Your token should already trade on Meteora (or a supported route). Create a pool or add liquidity, then configure the bot for that mint.",
      },
      {
        question: "Will this guarantee trending on DexScreener?",
        answer:
          "No tool can guarantee rankings. A volume bot increases on-chain trading activity teams use alongside marketing for DexScreener and discovery visibility.",
      },
      {
        question: "Which engine is best for a Meteora launch?",
        answer:
          "V4 or V6 for steady, budget-friendly activity; V12, V10, V8, or Mini Buy / Mini Buy-Sell for aggressive launch windows and busier charts.",
      },
      {
        question: "How much SOL do I need?",
        answer:
          "Minimums depend on the engine and whether it is your first charge. The recharge modal shows the required amount before you send funds.",
      },
      {
        question: "Is it safe to stop the bot?",
        answer:
          "Yes. Stop from your dashboard and refund unused gas according to the product rules—same controls as the main Solana volume bot.",
      },
    ],
  },
  idx: {
    slug: "idx",
    displayName: "IDX Launchlab",
    seoTitle: "IDX Volume Bot — Boost Your Launchlab Coin",
    seoDescription:
      "On-chain volume for IDX Launchlab tokens. Paste your mint, pick an engine, fund your bot, and earn creator fees through graduation.",
    ogAlt: "IDX Volume Bot — boost your Launchlab coin on Solana",
    ogImage: `${siteOrigin}/og-volume-bot.png`,
    schemaAppName: "IDX Launchlab Volume Bot",
    eyebrow: "IDX Launchlab & Raydium LaunchLab automation",
    h1Primary: "IDX Volume Bot",
    h1Secondary: "Market maker for your Launchlab coin — curve to graduation",
    heroParagraph:
      "Run an on-chain volume bot built for IDX Launchlab launches. Paste your token mint, choose an engine, and fund your bot in SOL or your own token. Increase trading activity and chart visibility on DexScreener while your coin is on the bonding curve or after it graduates to a Raydium CPMM pool — and earn creator fees on every trade.",
    howToSubtitle:
      "From wallet connect to on-chain execution—built for IDX Launchlab coin launches.",
    mintStepTitle: "Paste Launchlab coin mint",
    mintStepDetail:
      "Add your IDX Launchlab token mint address and confirm the symbol.",
    schemaMintStep:
      "Add your IDX Launchlab token mint address and confirm the symbol.",
    faq: [
      {
        question: "Does this work on the IDX Launchlab bonding curve before graduation?",
        answer:
          "Yes, for supported LaunchLab routes. Paste the mint from your Launchlab coin page, pick an engine, and fund the bot—the same flow as our main volume bot.",
      },
      {
        question: "What happens after my coin graduates to a Raydium pool?",
        answer:
          "Keep the same bot and mint. Activity continues on the migrated CPMM pool. You also receive a Creator Fee Key NFT for ongoing pool trade fees.",
      },
      {
        question: "Do I earn creator fees when the bot trades my coin?",
        answer:
          "Yes. IDX Launchlab coins accrue creator fees on every curve trade (0.5%) and pool trade fees after graduation via your Fee Key NFT. Claim from the Rewards page.",
      },
      {
        question: "Which engine is best for a new Launchlab coin?",
        answer:
          "V4 or V6 for early curve and tighter budgets; V12, V10, V8, or Mini Buy / Mini Buy-Sell for aggressive launch windows and busier charts.",
      },
      {
        question: "How much SOL do I need?",
        answer:
          "Minimums depend on the engine and whether it is your first charge. The recharge modal shows the required amount before you send funds.",
      },
      {
        question: "Is it safe to stop the bot?",
        answer:
          "Yes. Stop from your dashboard and refund unused gas according to the product rules—same controls as the main Solana volume bot.",
      },
    ],
  },
};

function buildPoolVolumeBotConfig(poolKey) {
  const c = POOL_VOLUME_BOT_CONTENT[poolKey];
  const canonicalPath = `/${c.slug}-volume-bot`;
  const steps = poolHowItWorksSteps(c.mintStepTitle, c.mintStepDetail);

  return {
    variant: poolKey,
    canonicalPath,
    seo: {
      title: c.seoTitle,
      description: c.seoDescription,
      ogTitle: `${c.seoTitle} | IDX Solana`,
      ogImage: c.ogImage ?? `${siteOrigin}/og-${c.slug}-volume-bot.png`,
      ogImageWidth: 1200,
      ogImageHeight: 630,
      ogAlt: c.ogAlt,
    },
    schema: {
      appName: c.schemaAppName,
      appDescription: c.seoDescription,
      howToName: `How to use ${c.displayName} volume bot`,
      howToSteps: [
        {
          name: "Connect Wallet",
          text: `Connect your Solana wallet before configuring your ${c.displayName} volume bot.`,
        },
        {
          name: c.mintStepTitle,
          text: c.schemaMintStep,
        },
        {
          name: "Pick engine and fund",
          text: `${ENGINE_PICK_STEP_DETAIL} Recharge in SOL and launch from your dashboard.`,
        },
      ],
    },
    hero: {
      eyebrow: c.eyebrow,
      h1Primary: `${c.displayName} Volume Bot`,
      h1Secondary: c.h1Secondary,
      heroParagraph: c.heroParagraph,
      mainAriaLabel: `${c.displayName} volume bot page`,
      titleId: `${c.slug}-volume-bot-page-title`,
      poolChips: "single",
      singlePoolKey: poolKey,
    },
    howItWorks: {
      headingBefore: "How ",
      headingHighlight: `${c.displayName} Volume Bot`,
      headingAfter: " Works",
      subtitle: c.howToSubtitle,
      steps,
    },
    explanation: {
      title: "What this tool does",
      variant: "pool",
      poolName: c.displayName,
      poolPath: canonicalPath,
    },
    faq: {
      title: `${c.displayName} volume bot FAQ`,
      items: c.faq,
    },
    crossLinkToPumpfun: false,
    crossLinkToVolumeBot: true,
  };
}

export const defaultVolumeBotLandingConfig = {
  variant: "default",
  canonicalPath: "/volume-bot",
  seo: {
    title: "Solana Volume Bot & Market Maker",
    description:
      "Solana volume bot for PumpFun, Raydium, Orca & Meteora. Boost SPL token volume and DexScreener visibility. Create your market maker bot in minutes.",
    ogTitle: "Solana Volume Bot & Market Maker | IDX Solana",
    ogImage: `${siteOrigin}/og-volume-bot.png`,
    ogImageWidth: 1600,
    ogImageHeight: 900,
    ogAlt: "Solana Volume Bot",
  },
  schema: {
    appName: "Solana Volume Bot & Market Maker",
    appDescription:
      "Solana volume bot for PumpFun, Raydium, Orca & Meteora. Boost SPL token volume and DexScreener visibility. Create your market maker bot in minutes.",
    howToName: "How to use Solana volume bot",
    howToSteps: [
      {
        name: "Connect Wallet",
        text: "Connect your Solana wallet and configure your bot settings.",
      },
      {
        name: "Choose Market",
        text: "Set your token pair, DEX market, and volume parameters.",
      },
      {
        name: "Launch Bot",
        text: "Create your bot and start boosting token volume and visibility across DEXs.",
      },
    ],
  },
  hero: {
    eyebrow: "Solana Automation Suite",
    h1Primary: "Solana Volume Bot",
    h1Secondary: "Market Maker — Boost Your SPL Token Volume & Visibility",
    heroParagraph:
      "Create an automated Solana market maker and volume bots for your Solana token on Pumpfun, Raydium and all other Solana pools. Lower cost than traditional market makers, with automated volume generation to boost visibility on DexScreener, CoinMarketCap, CoinGecko, and other Solana DEXs.",
    mainAriaLabel: "Solana volume bot page",
    titleId: "volume-bot-page-title",
    poolChips: "all",
  },
  howItWorks: {
    headingBefore: "How ",
    headingHighlight: "Volume Bot",
    headingAfter: " Works",
    subtitle:
      "Launch market activity in a structured flow, from wallet connection to bot execution.",
    steps: defaultHowItWorksSteps,
  },
  explanation: {
    title: "What this tool does",
    variant: "default",
  }, 
  faq: {
    title: "Solana volume bot FAQ",
    items: [
      {
        question: "What is a Solana volume bot?",
        answer:
          "A Solana volume bot automates on-chain trades for your SPL token to increase volume, transaction count, and chart activity on supported DEX pools including Pump.fun, Raydium, Orca, and Meteora.",
      },
      {
        question: "How does this market maker boost DexScreener visibility?",
        answer:
          "The bot executes real swaps on-chain, which can improve trading metrics that discovery platforms like DexScreener use when ranking and displaying active Solana pairs.",
      },
      {
        question: "Do I need a liquidity pool before starting?",
        answer:
          "Yes. Your token should already trade on a supported pool. Create a Solana token, add liquidity, then paste your mint address and choose an engine such as V4, V6, V8, V10, or V12.",
      },
      {
        question: "Which engine should I pick for a new SPL token?",
        answer:
          "V4 or V6 suit tighter launch budgets. V12, V10, V8, Mini Buy, and Mini Buy/Sell are common when teams want more aggressive Solana volume bot activity during early visibility pushes.",
      },
      {
        question: "Is IDX volume bot cheaper than traditional market makers?",
        answer:
          "IDX AutoBot is designed for lower operating cost than many manual market-making services, with transparent SOL recharge and dashboard controls to start, stop, and monitor bots.",
      },
      {
        question: "Can I run multiple volume bots at once?",
        answer:
          "Yes. Create multiple bots with different engines or tokens, fund each bot wallet, and manage them from the same IDX dashboard.",
      },
    ],
  },
  crossLinkToPumpfun: false,
  showPoolLandingLinks: true,
};

export const pumpfunVolumeBotLandingConfig =
  buildPoolVolumeBotConfig("pumpfun");
export const raydiumVolumeBotLandingConfig =
  buildPoolVolumeBotConfig("raydium");
export const orcaVolumeBotLandingConfig = buildPoolVolumeBotConfig("orca");
export const meteoraVolumeBotLandingConfig =
  buildPoolVolumeBotConfig("meteora");
export const idxVolumeBotLandingConfig = buildPoolVolumeBotConfig("idx");

/** Clone of General bot (/volume-bot) at a separate URL — not linked in sidebar. */
export const solanaMarketMakerVolumeBotLandingConfig = {
  ...defaultVolumeBotLandingConfig,
  variant: "solanaMarketMaker",
  canonicalPath: "/solana-market-maker",
  /** Same CreateBot UX as General bot (multi-DEX); own marketing copy. */
  createBotVariant: "solanaMarketMaker",
  seo: {
    title: "Solana Market Maker Bot",
    description:
      "Solana market maker for PumpFun, Raydium, Orca & Meteora. Run on-chain trades to lift DexScreener visibility. Launch in minutes.",
    ogTitle: "Solana Market Maker Bot | IDX Solana",
    ogImage: `${siteOrigin}/og-volume-bot.png`,
    ogImageWidth: 1600,
    ogImageHeight: 900,
    ogAlt: "Solana Market Maker",
  },
  schema: {
    appName: "Solana Market Maker Bot",
    appDescription:
      "Solana market maker for PumpFun, Raydium, Orca & Meteora. Run on-chain trades to lift DexScreener visibility. Launch in minutes.",
    howToName: "How to use Solana market maker",
    howToSteps: [
      {
        name: "Connect Wallet",
        text: "Connect your Solana wallet and configure your market maker settings.",
      },
      {
        name: "Choose Market",
        text: "Set your token pair, DEX market, and trading parameters.",
      },
      {
        name: "Launch Bot",
        text: "Create your market maker and start improving visibility across DEXs.",
      },
    ],
  },
  hero: {
    eyebrow: "Solana Trading Suite",
    h1Primary: "IDX Autobot",
    h1Secondary: "Grow Your SPL Token Visibility",
    h1SecondaryOnNewLine: true,
    heroParagraph:
      "Create a Solana market maker for your token on Pumpfun, Raydium, and all other Solana pools. Lower cost than traditional market makers, built for DexScreener, CoinMarketCap, CoinGecko, and other Solana DEXs.",
    mainAriaLabel: "Solana market maker page",
    titleId: "solana-market-maker-page-title",
    heroImageAlt: "Solana market maker illustration",
    poolChips: "all",
  },
  howItWorks: {
    headingBefore: "How ",
    headingHighlight: "Market Maker",
    headingAfter: " Works",
    subtitle:
      "Launch market activity in a structured flow, from wallet connection to bot execution.",
    steps: defaultHowItWorksSteps,
  },
  explanation: {
    title: "What this tool does",
    variant: "default",
  },
  faq: {
    title: "Solana market maker FAQ",
    subtitle:
      "Common questions about Solana market maker setup, engines, and funding",
    items: [
      {
        question: "What is a Solana market maker?",
        answer:
          "A Solana market maker runs on-chain trades for your SPL token to increase trading activity, transaction count, and chart presence on supported DEX pools including Pump.fun, Raydium, Orca, and Meteora.",
      },
      {
        question: "How does this market maker improve DexScreener visibility?",
        answer:
          "The bot executes real swaps on-chain, which can improve trading metrics that discovery platforms like DexScreener use when ranking and displaying active Solana pairs.",
      },
      {
        question: "Do I need a liquidity pool before starting?",
        answer:
          "Yes. Your token should already trade on a supported pool. Create a Solana token, add liquidity, then paste your mint address and choose an engine such as V4, V6, V8, V10, or V12.",
      },
      {
        question: "Which engine should I pick for a new SPL token?",
        answer:
          "V4 or V6 suit tighter launch budgets. V12, V10, V8, Mini Buy, and Mini Buy/Sell are common when teams want stronger market-maker presence during early visibility pushes.",
      },
      {
        question: "Is IDX market maker cheaper than traditional market makers?",
        answer:
          "IDX AutoBot is designed for lower operating cost than many manual market-making services, with transparent SOL recharge and dashboard controls to start, stop, and monitor bots.",
      },
      {
        question: "Can I run multiple market makers at once?",
        answer:
          "Yes. Create multiple bots with different engines or tokens, fund each bot wallet, and manage them from the same IDX dashboard.",
      },
    ],
  },
  toolAriaLabel: "Market maker configuration tool",
  crossLinkToPumpfun: false,
  showPoolLandingLinks: true,
};

/** Pair engine landing — trades token 1 with a second token in one bot. */
export const pairVolumeBotLandingConfig = {
  ...defaultVolumeBotLandingConfig,
  variant: "pair",
  canonicalPath: "/pair-bot",
  createBotVariant: "default",
  defaultPrefillEngine: "Pair",
  seo: {
    title: "Solana Pair Bot — Volume for Two Tokens",
    description:
      "Run one Solana pair bot that trades your token with a second token in one bot. Paste both mints, pick pools, fund in SOL, and print on-chain volume on both charts.",
    ogTitle: "Solana Pair Bot — Volume for Two Tokens | IDX Solana",
    ogImage: `${siteOrigin}/og-volume-bot.png`,
    ogImageWidth: 1600,
    ogImageHeight: 900,
    ogAlt: "Solana pair bot — trade your token with a second token in one bot",
  },
  schema: {
    appName: "Solana Pair Bot",
    appDescription:
      "IDX pair bot trades your SPL token with a second token in one bot, so one bot adds on-chain volume to both pairs.",
    howToName: "How to use the Solana pair bot",
    howToSteps: [
      {
        name: "Connect Wallet",
        text: "Connect your Solana wallet before configuring the pair bot.",
      },
      {
        name: "Paste both token mints",
        text: "Add your token mint, then the second token address, and confirm both symbols.",
      },
      {
        name: "Pick pools and launch",
        text: "Select the Pair engine, choose a pool for each token, recharge in SOL, and track both markets from the dashboard.",
      },
    ],
  },
  hero: {
    eyebrow: "Two tokens, one bot",
    h1Primary: "Solana Pair Bot",
    h1Secondary: "Trade your token with a second token in one bot",
    h1SecondaryOnNewLine: true,
    heroParagraph:
      "The Pair bot runs V4 Eco-style swaps with a second token in one bot, instead of quoting IDX. Paste both mints, pick a pool for each, and fund one bot so both charts get on-chain trades.",
    mainAriaLabel: "Solana pair bot page",
    titleId: "pair-bot-page-title",
    heroImageAlt: "Solana pair bot illustration",
    poolChips: "all",
  },
  howItWorks: {
    headingBefore: "How ",
    headingHighlight: "Pair Bot",
    headingAfter: " Works",
    subtitle:
      "One bot, two mints — from wallet connect to trades on both token pools.",
    steps: [
      {
        step: "01",
        title: "Connect Wallet",
        detail:
          "Connect your Solana wallet and verify your active account before configuration.",
      },
      {
        step: "02",
        title: "Paste both mints",
        detail:
          "Add your token mint, then the Token 2 address, and confirm both names.",
      },
      {
        step: "03",
        title: "Pick the Pair engine",
        detail:
          "Select Pair, then choose a pool for each token. Trades run about every 7–10 minutes at ~0.04 SOL per hour.",
      },
      {
        step: "04",
        title: "Fund & launch",
        detail:
          "Recharge in SOL, launch the bot, and watch both tokens from your dashboard.",
      },
    ],
  },
  explanation: {
    title: "What the pair bot does",
    variant: "pair",
  },
  faq: {
    title: "Solana pair bot FAQ",
    subtitle: "How the pair bot trades two tokens from one campaign",
    items: [
      {
        question: "What is the Solana pair bot?",
        answer:
          "Pair is a volume engine that trades your token with a second token in one bot, instead of quoting IDX. One bot adds on-chain trades to both markets.",
      },
      {
        question: "What do I need before I launch?",
        answer:
          "Both tokens should already trade on a supported pool. Paste token 1, paste token 2, pick a pool for each, then fund the bot in SOL.",
      },
      {
        question: "How often does it trade, and what does it cost?",
        answer:
          "Pair follows the V4 Eco cadence: about every 7–10 minutes, ~0.04 SOL per hour (~0.96 SOL per day), with about 80 SOL of volume and 125+ makers counted per token. The recharge screen shows the minimum before you send SOL.",
      },
      {
        question: "Will this guarantee DexScreener trending?",
        answer:
          "No. The bot increases real on-chain trade count on both tokens. Rankings still depend on broader demand and liquidity.",
      },
      {
        question: "Can I stop it?",
        answer:
          "Yes. Stop from the dashboard and refund unused gas under the same rules as other IDX volume bots.",
      },
    ],
  },
  showPoolLandingLinks: true,
};

export const VOLUME_BOT_LANDING_BY_VARIANT = {
  default: defaultVolumeBotLandingConfig,
  solanaMarketMaker: solanaMarketMakerVolumeBotLandingConfig,
  pumpfun: pumpfunVolumeBotLandingConfig,
  raydium: raydiumVolumeBotLandingConfig,
  orca: orcaVolumeBotLandingConfig,
  meteora: meteoraVolumeBotLandingConfig,
  idx: idxVolumeBotLandingConfig,
  pair: pairVolumeBotLandingConfig,
};

/** Engines hidden on all volume bot landing pages (embedded CreateBot). */
export const VOLUME_BOT_HIDDEN_ENGINE_IDS = [
  "V12Turbo",
  "PortfolioCombo",
];

export const ALL_VOLUME_BOT_LANDING_PATHS = [
  defaultVolumeBotLandingConfig.canonicalPath,
  solanaMarketMakerVolumeBotLandingConfig.canonicalPath,
  pairVolumeBotLandingConfig.canonicalPath,
  ...Object.values(POOL_VOLUME_BOT_CONTENT).map(
    (c) => `/${c.slug}-volume-bot`,
  ),
];

export function getVolumeBotLandingConfig(variant) {
  return VOLUME_BOT_LANDING_BY_VARIANT[variant] ?? defaultVolumeBotLandingConfig;
}

/** Resolve landing config from a pathname (e.g. `/pumpfun-volume-bot`). */
export function getVolumeBotLandingConfigByPath(pathname) {
  if (!pathname) return null;
  const cleaned = pathname.split("?")[0].split("#")[0];
  const normalized =
    cleaned === "/" ? "/" : cleaned.replace(/\/$/, "") || "/";
  return (
    Object.values(VOLUME_BOT_LANDING_BY_VARIANT).find(
      (config) => config.canonicalPath === normalized,
    ) ?? null
  );
}

export function buildVolumeBotMetadata(config) {
  const url = `${siteOrigin}${config.canonicalPath}`;
  return {
    title: config.seo.title,
    description: config.seo.description,
    alternates: { canonical: url },
    openGraph: {
      title: config.seo.ogTitle,
      description: config.seo.description,
      url,
      siteName: "IDX Solana",
      images: [
        {
          url: config.seo.ogImage,
          width: config.seo.ogImageWidth ?? 1200,
          height: config.seo.ogImageHeight ?? 630,
          alt: config.seo.ogAlt,
        },
      ],
      locale: "en_US",
      type: "website",
    },
    twitter: {
      card: "summary_large_image",
      title: config.seo.ogTitle,
      description: config.seo.description,
      images: [config.seo.ogImage],
    },
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        "max-video-preview": -1,
        "max-image-preview": "large",
        "max-snippet": -1,
      },
    },
  };
}

export function buildVolumeBotJsonLd(config) {
  const pageUrl = `${siteOrigin}${config.canonicalPath}`;
  const graph = [
    {
      "@type": "SoftwareApplication",
      name: config.schema.appName,
      description: config.schema.appDescription,
      url: pageUrl,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web",
    },
    {
      "@type": "HowTo",
      name: config.schema.howToName,
      step: config.schema.howToSteps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
    },
    {
      "@type": "VideoObject",
      name: VOLUME_BOT_DEMO_VIDEO.title,
      description: VOLUME_BOT_DEMO_VIDEO.description,
      thumbnailUrl: VOLUME_BOT_DEMO_VIDEO.posterUrl.startsWith("http")
        ? VOLUME_BOT_DEMO_VIDEO.posterUrl
        : `${siteOrigin}${VOLUME_BOT_DEMO_VIDEO.posterUrl}`,
      contentUrl: VOLUME_BOT_DEMO_VIDEO.url,
      uploadDate: VOLUME_BOT_DEMO_VIDEO.uploadDate,
      publisher: {
        "@type": "Organization",
        name: "IDX Solana",
      },
    },
  ];

  if (config.faq.items.length > 0) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: config.faq.items.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}
